import React, { useState, useMemo, useCallback } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import Loading from './Loading';
import './DemographicsMapView.css';

const DemographicsMapView = ({
  cities = [],
  totalCount = 0,
  type = 'followers',
  isLoading = false,
  onMarkerClick
}) => {
  const [selectedCity, setSelectedCity] = useState(null);

  // Use useLoadScript hook instead of LoadScript component to avoid multiple loads
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    preventGoogleFontsLoading: true, // Prevent loading Google Fonts
  });

  // Map container style
  const mapContainerStyle = {
    width: '100%',
    height: '100%',
    minHeight: '400px'
  };

  // Calculate center and zoom based on markers
  const { center, zoom } = useMemo(() => {
    if (!cities || cities.length === 0) {
      // Default: Center of Chile
      return { center: { lat: -33.4489, lng: -70.6693 }, zoom: 6 };
    }

    // Calculate bounds to fit all markers
    const bounds = {
      north: Math.max(...cities.map(c => c.latitude)),
      south: Math.min(...cities.map(c => c.latitude)),
      east: Math.max(...cities.map(c => c.longitude)),
      west: Math.min(...cities.map(c => c.longitude))
    };

    // Center point
    const center = {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2
    };

    // Zoom level based on bounds size
    const latDiff = bounds.north - bounds.south;
    const lngDiff = bounds.east - bounds.west;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom = 10;
    if (maxDiff > 20) zoom = 4;
    else if (maxDiff > 10) zoom = 5;
    else if (maxDiff > 5) zoom = 6;
    else if (maxDiff > 2) zoom = 7;
    else if (maxDiff > 1) zoom = 8;

    return { center, zoom };
  }, [cities]);

  // Calculate marker sizes based on count
  const getMarkerSize = useCallback((count) => {
    if (!totalCount || totalCount === 0) return 30;

    const percentage = (count / totalCount) * 100;

    // Scale markers: 20px (min) to 60px (max)
    if (percentage >= 20) return 60;
    if (percentage >= 15) return 50;
    if (percentage >= 10) return 40;
    if (percentage >= 5) return 30;
    return 20;
  }, [totalCount]);

  // Generate marker color based on type
  const getMarkerColor = useCallback(() => {
    switch(type) {
      case 'followers': return '#667eea'; // Brand blue
      case 'reached': return '#10b981';   // Green
      case 'engaged': return '#f59e0b';   // Orange
      default: return '#667eea';
    }
  }, [type]);

  // Handle marker click
  const handleMarkerClick = useCallback((city) => {
    setSelectedCity(city);
    if (onMarkerClick) {
      onMarkerClick(city);
    }
  }, [onMarkerClick]);

  const getDemographicLabel = () => {
    switch(type) {
      case 'followers': return 'seguidores';
      case 'reached': return 'alcanzados';
      case 'engaged': return 'comprometidos';
      default: return 'seguidores';
    }
  };

  if (isLoading || !isLoaded) {
    return <Loading fullScreen={false} />;
  }

  if (loadError) {
    return (
      <div className="map-empty">
        <p>⚠️ Error cargando Google Maps</p>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
          No se pudo cargar Google Maps. Verifica la API key.
        </p>
      </div>
    );
  }

  if (!cities || cities.length === 0) {
    return (
      <div className="map-empty">
        <p>📍 No hay datos de ciudades para mostrar</p>
      </div>
    );
  }

  return (
    <div className="demographics-map-container">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={zoom}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            zoomControl: true,
          }}
        >
          {/* Render markers for each city */}
          {isLoaded && window.google && cities.map((city, index) => {
            const size = getMarkerSize(city.count);

            return (
              <Marker
                key={`${city.city}-${index}`}
                position={{ lat: city.latitude, lng: city.longitude }}
                onClick={() => handleMarkerClick(city)}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: size / 2,
                  fillColor: getMarkerColor(),
                  fillOpacity: 0.6,
                  strokeColor: '#ffffff',
                  strokeWeight: 2,
                }}
              />
            );
          })}

          {/* Info window for selected marker */}
          {selectedCity && (
            <InfoWindow
              position={{
                lat: selectedCity.latitude,
                lng: selectedCity.longitude
              }}
              onCloseClick={() => setSelectedCity(null)}
            >
              <div className="map-info-window">
                <h4>📍 {selectedCity.city}</h4>
                <p className="info-count">
                  {selectedCity.count.toLocaleString()} {getDemographicLabel()}
                </p>
                <p className="info-percentage">
                  {((selectedCity.count / totalCount) * 100).toFixed(1)}% del total
                </p>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

      {/* Legend */}
      <div className="map-legend">
        <div className="legend-item">
          <div
            className="legend-circle legend-large"
            style={{ backgroundColor: getMarkerColor() }}
          ></div>
          <span>Mayor concentración</span>
        </div>
        <div className="legend-item">
          <div
            className="legend-circle legend-small"
            style={{ backgroundColor: getMarkerColor() }}
          ></div>
          <span>Menor concentración</span>
        </div>
      </div>
    </div>
  );
};

export default DemographicsMapView;
